import { useCallback, useEffect, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { InvitePersonForm } from './InvitePersonForm';
import { PendingInvitationsList } from './PendingInvitationsList';
import { canAccessProviderAdmin } from './providerAdminAccess';

export function PeoplePage() {
  const { session } = useAuth();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgsError, setOrgsError] = useState(null);
  const [invitesToken, setInvitesToken] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetBusyUserId, setResetBusyUserId] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState(null);
  const allowed = canAccessProviderAdmin(session);

  async function sendReset(row) {
    setResetBusyUserId(row.user_id);
    setResetResult(null);
    setResetError(null);
    try {
      const result = await providerAdminApi.sendPasswordReset(row.user_id);
      setResetResult(result);
    } catch (err) {
      setResetError(err.message || `Could not send a password reset to ${row.email}`);
    } finally {
      setResetBusyUserId(null);
    }
  }

  const load = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.listPeople({ q, page: 1, page_size: 100 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load people');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load('');
    else setLoading(false);
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    providerAdminApi
      .listOrganizations({ page: 1, page_size: 100 })
      .then((data) => {
        if (cancelled) return;
        setOrganizations(data?.items || []);
        setOrgsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrganizations([]);
        setOrgsError(err.message || 'Failed to load organizations');
      });
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed) {
    return <p className="text-sm text-eorig-muted">Provider Admin access is required.</p>;
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-eorig-ink">Invite a person</h2>
        <p className="text-sm text-eorig-muted">
          Invite someone as a Provider, Originator, Custodian or Investor user. Invite the same email
          more than once to give one person several workspaces.
        </p>
        {orgsError ? (
          <p className="text-sm text-red-600" role="alert">
            {orgsError}
          </p>
        ) : null}
        <InvitePersonForm
          organizations={organizations}
          onInvited={() => {
            setInvitesToken((value) => value + 1);
            return load(query);
          }}
        />
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-eorig-ink">Pending invitations</h2>
        <p className="text-sm text-eorig-muted">
          Resend mints a new one-time link (and re-sends the email when delivery is configured).
        </p>
        <PendingInvitationsList
          organizations={organizations}
          refreshToken={invitesToken}
          onChanged={() => load(query)}
        />
      </section>
      <h2 className="text-sm font-semibold text-eorig-ink">People in your organizations</h2>
      <p className="text-sm text-eorig-muted">
        Send password reset emails the person a one-time link, valid for one hour, to choose a
        new eVault password. It does not change their Microsoft account password.
      </p>
      {resetResult ? (
        <div
          role="status"
          className={`space-y-1 rounded-2xl border px-4 py-3 text-sm ${
            resetResult.email_delivery === 'sent'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <p>{resetResult.message}</p>
          {resetResult.reset_url ? (
            <p className="break-all">
              <span className="font-medium">Reset link (shown once):</span> {resetResult.reset_url}
            </p>
          ) : null}
        </div>
      ) : null}
      {resetError ? (
        <p className="text-sm text-red-600" role="alert">
          {resetError}
        </p>
      ) : null}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          load(query);
        }}
        className="flex gap-2"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email"
          className="w-full max-w-sm rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <button type="submit" className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white">
          Search
        </button>
      </form>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading people…</p>
      ) : (
        <DataTable
          columns={[
            { key: 'full_name', header: 'Name' },
            { key: 'email', header: 'Email' },
            { key: 'organization_code', header: 'Org' },
            { key: 'role', header: 'Role' },
            { key: 'status', header: 'Status' },
            {
              key: 'actions',
              header: 'Actions',
              render: (row) => (
                <button
                  type="button"
                  disabled={resetBusyUserId !== null || !row.is_active}
                  title={row.is_active ? undefined : 'Inactive accounts cannot reset their password'}
                  onClick={() => sendReset(row)}
                  className="rounded-lg border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-ink hover:bg-eorig-surface disabled:opacity-60"
                >
                  {resetBusyUserId === row.user_id ? 'Sending…' : 'Send password reset'}
                </button>
              ),
            },
          ]}
          rows={items}
          rowKey={(row) => row.membership_id}
          emptyMessage="No people in scoped organizations."
        />
      )}
    </div>
  );
}
